import { EDatasourceType, IDatasourceDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import KuikaEntitySwitcher, {
  KuikaEntitySwitcherItem
} from "../../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import { canManageDatasources, deleteDatasourceFromRow } from "../datasource-row-actions";
import { getEditorGlyph, IEditorGlyph } from "./editor-glyph";
import { EditorGlyphIcon } from "./editor-glyph-icon";
import { buildDatasourceSwitcherItemsOfTypes } from "./editor-switcher-items";
import { ISwitcherDelete, useSwitcherDelete } from "./use-switcher-delete";

/**
 * What the C# editor mounts for — the C# file itself plus the six provider kinds that are
 * edited in it (see the branch in `data-diagram-canvas.tsx`).
 */
export const CSHARP_EDITOR_DATASOURCE_TYPES: EDatasourceType[] = [
  EDatasourceType.CSharpFunctions,
  EDatasourceType.CustomAuthenticationProvider,
  EDatasourceType.CustomAuthorizationProvider,
  EDatasourceType.CustomVerificationCodeSenderProvider,
  EDatasourceType.CustomConnectionStringProvider,
  EDatasourceType.CustomExternalTokenValidatorProvider,
  EDatasourceType.CustomMailProvider
];

/**
 * The identity chip on a code editor's bar: which file is open — glyph, name, caret — and the
 * way to the other files this editor opens. The pencil beside it renames; the chip switches.
 *
 * A pick is PARKED as the editor's pending close, so the editor's guard offers to save first.
 */

export interface ICodeDatasourceSwitcherChipProps {
  sd: IScreenDesignContextState;
  setNextCallbackFunction: (callback?: () => void) => void;
  /** The family this editor mounts for — C# covers its provider types as well. */
  types: EDatasourceType[];
  current: IDatasourceDto | undefined;
  /** The open file's name — the editor's own committed copy, which a rename changes. */
  name: string;
  /**
   * What the open file is — known before the record exists, and for C# the template picked
   * rather than the saved type. It picks the glyph, its family colour and the tooltip naming
   * the kind ("C# Function", "Python Function").
   */
  datasourceType: EDatasourceType;
  entityLabel: string;
  triggerTestId?: string;
  /** On the name inside the chip: `<language>ActionNameLabel`. */
  labelTestId?: string;
  /** The editor's guarded close — it leads to the datasources list. */
  onManageAll: () => void;
}

export const CodeDatasourceSwitcherChip = ({
  sd,
  setNextCallbackFunction,
  types,
  current,
  name,
  datasourceType,
  entityLabel,
  triggerTestId,
  labelTestId,
  onManageAll
}: ICodeDatasourceSwitcherChipProps): React.ReactElement => {
  const glyph: IEditorGlyph = getEditorGlyph({ kind: "datasource", datasourceType });
  const items: KuikaEntitySwitcherItem[] = buildDatasourceSwitcherItemsOfTypes(sd.datasources ?? [], types);
  const find = (id: string): IDatasourceDto | undefined =>
    (sd.datasources ?? []).find((datasource: IDatasourceDto): boolean => datasource.id === id);
  const { deleteOption, deleteModal }: ISwitcherDelete<IDatasourceDto> = useSwitcherDelete(
    "datasource",
    (datasource: IDatasourceDto): void => void deleteDatasourceFromRow(sd, datasource)
  );

  // The datasources list's own rule for a datasource row.
  const mayManage: boolean = canManageDatasources(sd);

  return (
    <>
      <KuikaEntitySwitcher
        trigger="chip"
        triggerIcon={<EditorGlyphIcon glyph={glyph} />}
        triggerIconTooltip={glyph.label}
        triggerLabel={name}
        triggerLabelTestId={labelTestId}
        items={items}
        current={current ? { id: current.id, name } : undefined}
        entityLabel={entityLabel}
        iconName={glyph.iconName}
        iconType={glyph.iconType}
        triggerTestId={triggerTestId}
        onSelect={(item: KuikaEntitySwitcherItem): void => {
          const target: IDatasourceDto | undefined = (sd.datasources ?? []).find(
            (datasource: IDatasourceDto): boolean => datasource.id === item.id
          );
          if (!target) return;
          setNextCallbackFunction((): void => sd.setActiveEditingDatasource(target));
        }}
        onCreateNew={
          mayManage
            ? (): void => {
                // Creating one starts by picking a runtime, and that screen is the landing page's.
                setNextCallbackFunction((): void => sd.requestDatasourceCreate("function"));
              }
            : undefined
        }
        onManageAll={onManageAll}
        getRowActions={(item: KuikaEntitySwitcherItem) => {
          const datasource: IDatasourceDto | undefined = find(item.id);
          return datasource ? [deleteOption(datasource, !mayManage)] : [];
        }}
      />
      {deleteModal}
    </>
  );
};
